import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import MessageAlert from 'app/components/MessageAlert'
import effects from 'app/effects/effects'
import { useFlag } from '@unleash/proxy-client-react'

export default function useAddOneByOne() {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()
  const [errorCode, setErrorCode] = useState(null)
  const [isLoading, setIsLoaging] = useState(false)

  const hasAlphaNumericError = useCallback(
    (messages) => messages.includes('validation.error.alpha_numeric'),
    [],
  )

  const successfullyMessage = formatMessage({
    id: 'subscriber.addOneByOne.successfullyMessage',
  })

  const failedMessage = formatMessage({
    id: 'subscriber.addOneByOne.failedMessage',
  })

  const generateErrorKeys = useCallback(
    (errors) => {
      return Object.entries(errors).reduce((acc, [field, messages]) => {
        if (hasAlphaNumericError(messages)) {
          const msg = formatMessage({
            id: `${field}.validation.error.alpha_numeric`,
          })
          acc.push(msg)
        } else {
          acc.push(messages[0])
        }
        return acc
      }, [])
    },
    [formatMessage, hasAlphaNumericError],
  )
  const dispatchErrorMessage = useCallback(
    (errorKeys) => {
      if (errorKeys.length > 0) {
        dispatch(
          MessageAlert.showError({
            message: (
              <div>
                {errorKeys.map((error, index) => (
                  <p key={index}>{error}</p>
                ))}
              </div>
            ),
          }),
        )
      }
    },
    [dispatch],
  )

  const createSubscriber = useCallback(
    async (formData) => {
      try {
        setIsLoaging(true)
        await effects.apis.createSubscriber(formData)
        setErrorCode(null)
        dispatch(MessageAlert.showSuccess({ message: successfullyMessage }))
        return true
      } catch (error) {
        const { details } = error?.response?.data || {}

        if (!details) return

        const { code, message } = details

        setErrorCode(code)

        if (code === 602 && message.errors) {
          const errorKeys = generateErrorKeys(message.errors)
          dispatchErrorMessage(errorKeys)
        } else {
          dispatch(
            MessageAlert.showError({ message: message || failedMessage }),
          )
        }
        return false
      } finally {
        setIsLoaging(false)
      }
    },
    [
      dispatch,
      dispatchErrorMessage,
      failedMessage,
      generateErrorKeys,
      successfullyMessage,
    ],
  )

  const handleCreateSubscriber = async (values) => {
    const {
      phoneNumber,
      automatedCallPhoneNumber,
      countryCode,
      automatedCallCountryCode,
    } = values
    const cleanedPhoneNumber = phoneNumber.replace(countryCode, '')
    const cleanedAutomatedCallPhoneNumber = automatedCallPhoneNumber.replace(
      automatedCallCountryCode,
      '',
    )

    const formData = {
      ...values,
      countryCode,
      automatedCallCountryCode,
      phoneNumber: cleanedPhoneNumber,
      automatedCallPhoneNumber: cleanedAutomatedCallPhoneNumber,
      email: values.email.trim(),
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
    }

    if (!cleanedPhoneNumber) {
      formData.countryCode = ''
    }
    if (!cleanedAutomatedCallPhoneNumber) {
      formData.automatedCallCountryCode = ''
    }

    return await createSubscriber(formData)
  }

  return {
    errorCode,
    isLoading,
    handleCreateSubscriber,
  }
}
